"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ConnectAgencyClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  agencyName: string;
  clientName?: string;
  isConnecting?: boolean;
}

/**
 * Confirmation before a venue is connected to an agency.
 *
 * DELIBERATELY NOT DESTRUCTIVE-LOOKING. Its sibling —
 * `DisconnectAgencyClientDialog` — is the last thing between a mis-click and
 * permanent loss, and the two must not be mistaken for one another at a
 * glance. This one keeps the default action button and states a consequence
 * that is additive: the agency gains access, and both sides are told.
 *
 * Nothing is validated here. An unknown venue, a venue without marketing, and
 * a pair already connected are all the server's refusals to make, and the
 * caller renders whichever comes back rather than pre-checking.
 */
export function ConnectAgencyClientDialog({
  open,
  onOpenChange,
  onConfirm,
  agencyName,
  clientName,
  isConnecting = false,
}: ConnectAgencyClientDialogProps) {
  // The dialog stays mounted between opens, so it renders once with nothing
  // selected; the name must never come out as an empty pair of quotes.
  const name = clientName?.trim();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          {/* `break-words`: a venue name can be long and unbroken, and the
              card is a fixed 32rem. */}
          <AlertDialogTitle className="break-words">
            {name
              ? `Connect "${name}" to "${agencyName}"?`
              : "Connect this client?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This agency will be able to work on that venue&apos;s content, and
            the venue will appear among its clients. Every admin at the venue
            and at the agency is emailed about the connection.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isConnecting}>Cancel</AlertDialogCancel>
          {/* `preventDefault`: Radix closes the dialog on the action's click
              by default, which would tear it down before the request resolves
              — the pending label could never render and a refusal would land
              with the dialog already gone. The caller closes it from the
              mutation's callbacks instead. `DeleteVenueDialog` holds its own
              open the same way. */}
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
            disabled={isConnecting}
          >
            {isConnecting ? "Connecting..." : "Connect client"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
